import { useZero } from "@rocicorp/zero/react";
import { keyframes, styled } from "goober";
import { useCallback, useEffect } from "react";
import { findConnectorTypes } from "../../shared/connectors/findConnectorTypes";
import { getConnectorAiContext } from "../../shared/connectors/getAiContext";
import { sharedMutators } from "../../shared/mutators";
import type { ChatTool, ToolUseProps } from "../components/Chat/ChatTool";
import { Icon } from "../components/Icon";
import { ShimmerSpan } from "../components/ShimmerSpan";
import { Text } from "../components/Text";
import { TypewriterSpan } from "../components/TypewriterSpan";
import { useGetPresence } from "../hooks/useGetPresence";
import { useCurrentUserId } from "../hooks/useCurrentUserId";
import { useNavigateOnBranchChange } from "../hooks/useNavigateOnBranchChange";
import { maybeDisplayBrowserNotification } from "../lib/browserNotifications";
import { otherCollaboratorsPresent } from "../lib/connectPrompt";
import { type SelectableConnector } from "../hooks/useConnectorSelectOnSave";
import { queries } from "../../shared/queries";
import { useQuery } from "../hooks/useQuery";
import { navigate, routes, useCurrentUrl } from "../lib/urls";
import {
  ConfirmPrompt,
  ConnectorPrompt,
  mutationErrorMessage,
} from "./ConnectorPrompt";

export type ConnectToAppArgs =
  | { branchId: string; personalChatId?: never }
  | { personalChatId: string; branchId?: never };

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Rotating = styled("span")`
  &[data-pending="true"] span {
    animation: ${spin} 3s linear infinite;
  }
`;

type Input = {
  step?: string;
  targetUrl?: string;
  search?: string;
  connectorId?: string;
};
type Output =
  | { connected: true; connectedAppName: string; appIdentifier?: string }
  | { dismissed: true }
  | { noAppsAvailable: true; error: string }
  | { error: string };

type ResultHandler = (
  output: Output & { step?: string; aiContext?: string },
  isError: boolean,
  options?: { pauseAfter?: boolean }
) => void;

function connectedOutput(app: SelectableConnector, step?: string) {
  const aiContext = getConnectorAiContext(app);
  return {
    connected: true as const,
    connectedAppName: app.name,
    appIdentifier: app.identifier,
    ...(step && { step }),
    ...(aiContext && { aiContext }),
  };
}

function isPersonalConnectArgs(
  args: ConnectToAppArgs
): args is Extract<ConnectToAppArgs, { personalChatId: string }> {
  return typeof args.personalChatId === "string";
}

function outputKind(
  output: Output
): "connected" | "dismissed" | "noAppsAvailable" | "error" {
  if ("noAppsAvailable" in output) return "noAppsAvailable";
  if ("connected" in output) return "connected";
  if ("dismissed" in output) return "dismissed";
  return "error";
}

function ConnectToAppHeader({ toolUse, result, interrupted }: ToolUseProps) {
  const input = toolUse.input as Input | undefined;
  const output = result?.output as Output | undefined;
  const stepName = input?.step;
  const targetLabel = input?.targetUrl ?? input?.search ?? "connector";

  if (!output) {
    const pending = !interrupted;
    return (
      <>
        <Rotating data-pending={pending}>
          <Icon name="circle-dashed" size={12} color="var(--text-secondary)" />
        </Rotating>
        <Text variant="text-small" color="var(--text-secondary)" ellipsis>
          <ShimmerSpan data-pending={pending}>
            <TypewriterSpan>{`Asking to connect ${targetLabel}…`}</TypewriterSpan>
          </ShimmerSpan>
        </Text>
      </>
    );
  }

  switch (outputKind(output)) {
    case "noAppsAvailable":
      return (
        <>
          <Icon name="check-empty" size={12} color="var(--text-secondary)" />
          <Text variant="text-small" color="var(--text-secondary)" ellipsis>
            <TypewriterSpan>{`No matching connectors available for ${targetLabel}`}</TypewriterSpan>
          </Text>
        </>
      );
    case "error":
      return (
        <>
          <Icon name="circle-off" size={12} color="var(--red-600)" />
          <Text variant="text-small" color="var(--text-secondary)" ellipsis>
            <TypewriterSpan>{`Failed to connect ${targetLabel}`}</TypewriterSpan>
          </Text>
        </>
      );
    case "dismissed":
      return (
        <>
          <Icon name="check-empty" size={12} color="var(--text-secondary)" />
          <Text variant="text-small" color="var(--text-secondary)" ellipsis>
            <TypewriterSpan>{`Cancelled connecting ${targetLabel}`}</TypewriterSpan>
          </Text>
        </>
      );
    case "connected":
      return (
        <>
          <Icon name="check-filled" size={12} color="var(--green-600)" />
          <Text variant="text-small" color="var(--text-secondary)" ellipsis>
            <TypewriterSpan>
              {stepName
                ? `Connected ${(output as Extract<Output, { connected: true }>).connectedAppName} to ${stepName}`
                : `Connected ${(output as Extract<Output, { connected: true }>).connectedAppName}`}
            </TypewriterSpan>
          </Text>
        </>
      );
  }
}

function ConnectToAppBody({ result }: ToolUseProps) {
  const output = result?.output as Output | undefined;
  if (!output) return null;
  if ("error" in output) {
    return (
      <Text variant="text-small" color="var(--text-secondary)">
        {output.error}
      </Text>
    );
  }
  return null;
}

function useAssociateStepWithApp(
  workflowBranchId: string,
  onResult: ResultHandler
) {
  const z = useZero();
  const navigateOnBranchChange = useNavigateOnBranchChange(workflowBranchId);
  const {
    spaceId,
    workflowId,
    step: urlStep,
  } = useCurrentUrl(routes.space.workflow);
  return useCallback(
    async (app: SelectableConnector, stepDir: string) => {
      let serverPromise: Promise<{ type: string; error?: unknown }>;
      const forkBranchId = await navigateOnBranchChange((branchParams) => {
        const result = z.mutate(
          sharedMutators.associateStepWithConnectedApp({
            ...branchParams,
            stepDir,
            connectedAppId: app.id,
            silent: true,
          })
        );
        serverPromise = result.server;
        return result;
      });
      const serverResult = await serverPromise!;
      if (serverResult.type === "error") {
        onResult({ error: mutationErrorMessage(serverResult.error) }, true);
        return;
      }
      if (!forkBranchId) {
        navigate(
          routes.space.workflow,
          { spaceId, workflowId, step: urlStep },
          { replace: true }
        );
      }
      onResult(connectedOutput(app, stepDir), false);
    },
    [navigateOnBranchChange, onResult, spaceId, urlStep, workflowId, z]
  );
}

function PendingConnectToAppPrompt({
  args,
  pending,
  onResult,
}: {
  args: ConnectToAppArgs;
  pending: Input & { toolCallId: string; appIdentifiers: string[] };
  onResult: ResultHandler;
}) {
  useEffect(() => {
    const target = pending.targetUrl ?? pending.search ?? "a connector";
    maybeDisplayBrowserNotification("3B needs you", `Connect to ${target}`);
  }, [pending.targetUrl, pending.search]);

  const z = useZero();
  const personal = isPersonalConnectArgs(args);
  const personalChatId = personal ? args.personalChatId : "";
  const workflowBranchId = personal ? "" : args.branchId;
  const { spaceId } = useCurrentUrl(routes.space.workflow);
  const presence = useGetPresence();
  const currentUserId = useCurrentUserId();
  const othersPresent = otherCollaboratorsPresent({
    personal,
    currentUserId,
    presentUserIds: presence.presentByBranch.get(workflowBranchId) ?? [],
  });

  const associateStep = useAssociateStepWithApp(workflowBranchId, onResult);
  const handleSelectApp = useCallback(
    async (app: SelectableConnector) => {
      if (!personal && pending.step) {
        await associateStep(app, pending.step);
        return;
      }
      // Personal chats record an attachment; build chats record the
      // workflow-wide chat confirmation — picking here is the confirmation
      // moment, so connectors created or authorized through this picker
      // never re-prompt.
      const result = z.mutate(
        personal
          ? sharedMutators.associatePersonalChatWithConnectedApp({
              personalChatId,
              connectedAppId: app.id,
            })
          : sharedMutators.associateBranchChatWithConnectedApp({
              branchId: workflowBranchId,
              connectedAppId: app.id,
            })
      );
      const serverResult = await result.server;
      if (serverResult.type === "error") {
        onResult({ error: mutationErrorMessage(serverResult.error) }, true);
        return;
      }
      onResult(connectedOutput(app), false);
    },
    [
      associateStep,
      onResult,
      pending.step,
      personal,
      personalChatId,
      workflowBranchId,
      z,
    ]
  );

  return (
    <ConnectorPrompt
      pending={pending}
      othersPresent={othersPresent}
      spaceId={personal ? undefined : spaceId}
      openCreatedApp={(connectedAppId) => {
        navigate(routes.modal.connector, { id: connectedAppId });
      }}
      onSelectApp={handleSelectApp}
      onDismiss={() => onResult({ dismissed: true }, false)}
      onError={(message) => onResult({ error: message }, true)}
      onNoApps={
        personal
          ? () =>
              onResult(
                {
                  noAppsAvailable: true,
                  error:
                    "No connectors match this request and the user does not have permission to create new ones. Tell the user they need to ask a tenant admin to create or grant access to a connector.",
                },
                false
              )
          : undefined
      }
      onTypeInstead={() =>
        onResult({ dismissed: true }, false, { pauseAfter: true })
      }
    />
  );
}

// The confirmation card for attaching a connector the agent named by id to a
// step: the confirmation is the attach itself, asked for because the workflow
// doesn’t use the connector yet.
function PendingConfirmPrompt({
  branchId,
  connectorId,
  step,
  onResult,
}: {
  branchId: string;
  connectorId: string;
  step: string;
  onResult: ResultHandler;
}) {
  const [apps] = useQuery(queries.connectedAppsIndex());
  const app = apps.find((a) => a.id === connectorId);
  const associateStep = useAssociateStepWithApp(branchId, onResult);

  useEffect(() => {
    if (!app) return;
    maybeDisplayBrowserNotification(
      "3B needs you",
      `Connect ${app.name} to ${step}`
    );
  }, [app, step]);

  const handleConfirm = useCallback(async () => {
    if (!app) return;
    await associateStep(app, step);
  }, [app, associateStep, step]);

  if (!app) return null;
  return (
    <ConfirmPrompt
      title={`Connect ${app.name} to ${step}?`}
      description={`The agent wants to attach the ${app.name} connector to the “${step}” step.`}
      hint="The step will authenticate with this connector for everyone who runs the workflow."
      confirmLabel="Connect to step"
      onConfirm={handleConfirm}
      onDismiss={() => onResult({ dismissed: true }, false)}
      onTypeInstead={() =>
        onResult({ dismissed: true }, false, { pauseAfter: true })
      }
    />
  );
}

export const connectToApp = (args: ConnectToAppArgs): ChatTool => ({
  name: "connectToApp",
  Header: ConnectToAppHeader,
  Body: ConnectToAppBody,
  alwaysLoadOutput: true,
  renderPendingPrompt: ({ toolCallId, input, onResult }) => {
    const parsed = input as Input | null;
    if (!parsed) return null;
    if (parsed.connectorId && parsed.step && !isPersonalConnectArgs(args)) {
      return (
        <PendingConfirmPrompt
          branchId={args.branchId}
          connectorId={parsed.connectorId}
          step={parsed.step}
          onResult={onResult as ResultHandler}
        />
      );
    }
    // A chat-scoped connectorId renders the same connector prompt a step
    // would, narrowed to that connector — picking it is the confirmation.
    const chatConnectorId =
      parsed.connectorId && !isPersonalConnectArgs(args)
        ? parsed.connectorId
        : undefined;
    if (!chatConnectorId && !parsed.targetUrl && !parsed.search) return null;
    const appIdentifiers = findConnectorTypes(parsed).map(
      (app) => app.identifier
    );

    return (
      <PendingConnectToAppPrompt
        args={args}
        pending={{
          toolCallId,
          step: parsed.step,
          targetUrl: parsed.targetUrl,
          search: parsed.search,
          connectorId: chatConnectorId,
          appIdentifiers,
        }}
        onResult={onResult as ResultHandler}
      />
    );
  },
});
